import { useEffect, useMemo, useState } from "react";
import api, { inr } from "@/lib/api";
import { useCart } from "@/lib/cart";
import DashboardLayout from "@/components/DashboardLayout";
import { Link } from "react-router-dom";
import { ShoppingBag, Plus, Search, ShoppingCart, Package, Clock, Leaf, Sparkles, Gift, Shirt, Wheat, GraduationCap } from "lucide-react";
import { toast } from "sonner";

// Canonical 6 categories — SAME labels as public storefront + admin editor.
const CATS = [
  { key: "all",          label: "All",                    icon: Package     },
  { key: "health",       label: "Health & Wellness",      icon: Leaf        },
  { key: "personal_care",label: "Personal Care",          icon: Sparkles    },
  { key: "combo",        label: "Combos",                 icon: Gift        },
  { key: "fashion",      label: "Fashion & Apparel",      icon: Shirt       },
  { key: "agriculture",  label: "Agriculture",            icon: Wheat       },
  { key: "education",    label: "Education",              icon: GraduationCap},
];

/** IBA Shopping page — same product set as public + admin.
 *  Coming Soon products render disabled (grey out + no Add button) so IBAs
 *  can preview upcoming SKUs without accidentally purchasing them.
 *  Calculation logic (SP · DP · BV) is UNCHANGED — only presentation differs.
 */
export default function AssociateShop() {
  const { add, count } = useCart();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products?limit=200").then((r) => {
      const list = (r.data.items || []).filter((p) => (p.sku || "").startsWith("SW-"));
      setProducts(list);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = products;
    if (cat !== "all") out = out.filter((p) => p.category === cat);
    if (term) {
      out = out.filter((p) => {
        const hay = [p.name, p.sku, p.description, p.category].filter(Boolean).join(" ").toLowerCase();
        return term.split(/\s+/).every((t) => hay.includes(t));
      });
    }
    // Live products first, Coming Soon at the end (still visible & disabled)
    return [...out].sort((a, b) => (a.is_coming_soon ? 1 : 0) - (b.is_coming_soon ? 1 : 0));
  }, [products, q, cat]);

  return (
    <DashboardLayout
      title="Shop Products"
      subtitle="Buy at your Distributor Price (DP) and earn Sales Points (SP). Coming Soon products are preview-only."
    >
      {/* Search + cart */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <div className="md:col-span-3 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            data-testid="shop-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, SKU or description…"
            className="w-full pl-10 pr-3 py-3 border border-line rounded-sm text-sm focus:border-gold outline-none"
          />
        </div>
        <Link
          to="/cart"
          data-testid="shop-view-cart"
          className="px-4 py-3 bg-royal text-white rounded-sm inline-flex items-center justify-center gap-2 hover:bg-royal-deep text-sm"
        >
          <ShoppingCart size={16} /> View Cart {count > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-gold text-royal text-[10px] rounded-full font-bold">{count}</span>
          )}
        </Link>
      </div>

      {/* Category tabs */}
      <div className="flex items-center gap-2 flex-wrap mb-6 border-b border-line pb-4 overflow-x-auto">
        {CATS.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              onClick={() => setCat(c.key)}
              data-testid={`shop-cat-${c.key}`}
              className={`shrink-0 px-4 py-2 text-xs rounded-sm transition-all border inline-flex items-center gap-1.5 ${
                cat === c.key
                  ? "bg-royal text-white border-royal"
                  : "bg-white text-royal/70 border-line hover:border-gold"
              }`}
            >
              <Icon size={12} /> {c.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-navy/50 text-sm py-16 text-center">Loading catalog…</div>
      ) : visible.length === 0 ? (
        <div className="py-16 text-center text-navy/50 text-sm">
          <Package className="mx-auto text-navy/30 mb-3" size={32} />
          No products match your search / filter.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {visible.map((p) => (
            <AssocProductCard
              key={p.id}
              product={p}
              onAdd={() => { add(p); toast.success(`${p.name} added to cart`); }}
            />
          ))}
        </div>
      )}

      <div className="mt-10 pt-6 border-t border-line text-xs text-navy/60 flex flex-wrap items-center gap-3">
        <ShoppingBag size={13} className="text-gold" />
        Orders here are billed at your Distributor Price (DP) and add SP to your left/right leg for weekly matching.
        <Link to="/my-orders" className="text-royal hover:text-gold underline">View my past orders →</Link>
      </div>
    </DashboardLayout>
  );
}

function AssocProductCard({ product, onAdd }) {
  const sp = product.sp ?? product.sp;
  const isComingSoon = !!product.is_coming_soon;
  return (
    <div
      data-testid={`assoc-product-${product.id}`}
      className={`group bg-white border rounded-sm overflow-hidden flex flex-col transition-all ${
        isComingSoon
          ? "border-line opacity-80"
          : "border-line hover:border-gold/50 hover:shadow-md"
      }`}
    >
      <Link
        to={isComingSoon ? "#" : `/product/${product.id}`}
        onClick={(e) => { if (isComingSoon) { e.preventDefault(); toast.info(`${product.name} — Coming Soon`); } }}
        className="block relative aspect-square bg-ivory overflow-hidden"
      >
        <img
          src={product.image
            ? `${product.image}${product.image.includes("?") ? "&" : "?"}v=${product.updated_at || product.id}`
            : "/product-images/coming-soon.svg"}
          alt={product.name}
          loading="lazy"
          onError={(e) => { e.currentTarget.src = "/product-images/coming-soon.svg"; }}
          className={`w-full h-full object-cover transition-transform duration-500 ${
            isComingSoon ? "grayscale opacity-70" : "group-hover:scale-105"
          }`}
        />
        <span className="absolute top-3 left-3 bg-white/95 text-royal text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm">
          {(product.category || "").replace(/_/g, " ")}
        </span>
        {!isComingSoon && (
          <span data-testid={`assoc-sp-${product.id}`} className="absolute top-3 right-3 bg-royal text-gold text-[10px] font-bold px-2 py-1">
            {Number.isInteger(sp) ? sp : Number(sp).toFixed(2)} SP
          </span>
        )}
        {isComingSoon && (
          <span data-testid={`assoc-cs-${product.id}`} className="absolute inset-x-0 bottom-0 bg-royal/95 text-gold text-[10px] font-semibold uppercase tracking-widest px-3 py-2 inline-flex items-center justify-center gap-1.5">
            <Clock size={11} /> Coming Soon
          </span>
        )}
      </Link>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] text-navy/40 uppercase tracking-widest font-mono">{product.sku}</div>
        <div className="font-serif text-base text-royal mt-1 line-clamp-2 leading-tight">{product.name}</div>
        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gold-deep">Your Price</div>
            <div className="text-xl text-royal font-extrabold tabular-nums lining-nums">{inr(product.dp)}</div>
            {product.mrp > product.dp && (
              <div className="text-[10px] text-navy/40 line-through tabular-nums lining-nums">MRP {inr(product.mrp)}</div>
            )}
          </div>
          {isComingSoon ? (
            <button
              disabled
              data-testid={`assoc-disabled-${product.id}`}
              title="Coming Soon — not yet available for purchase"
              className="px-3 py-2 bg-navy/10 text-navy/40 text-xs rounded-sm inline-flex items-center gap-1 cursor-not-allowed"
            >
              <Clock size={13} /> Soon
            </button>
          ) : (
            <button
              onClick={onAdd}
              data-testid={`assoc-add-${product.id}`}
              className="px-3 py-2 bg-royal text-white text-xs hover:bg-royal-deep rounded-sm inline-flex items-center gap-1"
            >
              <Plus size={13} /> Add
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
